import {
  AfterViewInit,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  Renderer2,
  Self,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormControl, NgControl } from '@angular/forms';
import { MAT_FORM_FIELD, MatFormField } from '@angular/material/form-field';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CssClasses } from '@shared/classes/css-constants';
import {
  ControlBorderDirective
} from '@shared/components/most-used/form-controls-errors/control-border.directive';
import {
  ControlWrapperDirective
} from '@shared/components/most-used/form-controls-errors/control-wrapper.directive';
import { ActivityService } from '@shared/services/activity.service';
import { GetRand } from '@shared/utils/mix.utils';
// eslint-disable-next-line max-len
import { BehaviorSubject, EMPTY, merge, Observable, Subject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { FormErrorComponent } from '../form-error/form-error-component/form-error.component';
import { customFormErrors, defaultFormErrors, ErrorDictionary } from '../form-error/form-errors.list';
import { FormEventsDirective } from '../form-error/form-events.directive';

interface IFormError {
  key: string;
  text: string;
}

@UntilDestroy()
@Directive({
  selector: '[appFV]',
})
export class FormControlErrorsDirective implements OnInit, AfterViewInit {
  /** if passed, this control will be used insted of ngControl **/
  @Input('appFVControl') passedFormControl: UntypedFormControl;

  /** show error even if elements focused **/
  @Input('appFVEvenIfFocused') evenIfFocused: boolean;

  /** if passed touch events inside element with this class name will be considered as events inside element **/
  @Input('appFVOverlayMarker') overlayMarker: string;

  /** if appFVExtSrc provided, it's value (true means valid) will be used insted of control's validation value **/
  @Input('appFVManagedEls') externalManagedElements: ElementRef[] | HTMLElement[];

  @Input('appFVOnlyBorder') onlyBorder: boolean;
  @Input('appFVDisabled') validationDisabled: boolean;
  @Input('appFVDebugId') debugId: string;

  @Output('appFVFocusChanged') focusChangedEventEmitter = new EventEmitter<boolean>();

  errorComponentRef: ComponentRef<FormErrorComponent>;
  container: ViewContainerRef;
  borderContainer: ViewContainerRef;
  formUpdate$: Observable<any>;
  updateControl$ = new Subject();
  globalFocusStatus$ = new BehaviorSubject<boolean | undefined>(undefined);
  errors: ErrorDictionary;
  control: UntypedFormControl;
  private nativeElement: HTMLElement;
  private tmpId = GetRand();

  constructor(
    private renderer2: Renderer2,
    private elRef: ElementRef,
    private vcr: ViewContainerRef,
    private resolver: ComponentFactoryResolver,
    private activityService: ActivityService,
    @Self() @Optional() private ngControl: NgControl,
    @Optional() private updateEvents: FormEventsDirective,
    @Optional() private controlWrapperDirective: ControlWrapperDirective,
    @Optional() private controlBorderDirective: ControlBorderDirective,
    @Optional() @Inject(MAT_FORM_FIELD) private _formField?: MatFormField,
  ) {
    this.errors = {
      ...defaultFormErrors,
      ...customFormErrors,
    };
  }

  @HostListener('focus')
  handleFocus() {
    // this.hostIsFocused = true;
    this.globalFocusStatus$.next(true);
  }

  ngOnInit() {
    if (this.validationDisabled) {
      return;
    }
    this.trackFocus();
    this.nativeElement = this.elRef.nativeElement;

    this.globalFocusStatus$.pipe(untilDestroyed(this)).subscribe({
      next: (focused) => {
        this.toggleBorderByFocus(focused);
        this.toggleErrorComponent(focused);
        this.focusChangedEventEmitter.next(focused);
      }
    })
  }

  ngAfterViewInit() {
    if (this.validationDisabled) {
      return;
    }
    if (this.passedFormControl) {
      this.control = this.passedFormControl;
    } else if (this.ngControl) {
      this.control = this.ngControl.control as unknown as UntypedFormControl;
    } else {
      console.error('Cannot find control', this.ngControl, this.passedFormControl);
    }

    this.formUpdate$ = this.updateEvents ? this.updateEvents.formEvents.asObservable() : EMPTY;
    setTimeout(() => {
      this.container = this.controlWrapperDirective?.vcr || this.vcr;
      this.borderContainer = this.controlBorderDirective?.elRef.nativeElement || this.elRef.nativeElement;
      if (this.borderContainer && this.control.untouched) {
        this.toggleUntouchedClass(false);
      }

      this.getPossibleEvents().pipe(untilDestroyed(this)).subscribe();
      this.patchControl();

      if (this.debugId) {
        console.log('AppFV: control', this.control);
        console.log('AppFV: controlParent', this.control.parent);
      }
    }, 0);

    // if (this.externalValidationSrc) {
    //   this.externalValidationSrc.pipe(untilDestroyed(this), filter(v => v !== undefined)).subscribe({
    //     next: (valid) => {
    //       console.log('EEEE', valid, this.tmpId);
    //       // this.toggleUntouchedClass(true);
    //       this.toggleErrorAndBorder(!valid);
    //     }
    //   })
    // }
  }

  patchControl() {
    const control = this.control;
    if (!control) {
      console.error('Cannot find control to patch', this);
      return;
    }
    const mark = control.markAsTouched;
    control.markAsTouched = () => {
      mark.call(control);
      this.updateControl$.next();
      this.toggleUntouchedClass(true);
    };
    const markAsUntouched = control.markAsUntouched;
    control.markAsUntouched = () => {
      markAsUntouched.call(control);
      this.updateControl$.next();
      this.toggleUntouchedClass(false);
    };
  }

  getPossibleEvents() {
    return merge(
      this.formUpdate$,
      this.updateControl$,
      this.globalFocusStatus$.pipe(filter(v => v !== undefined)),
      this.control ? this.control.valueChanges : EMPTY,
      this.control ? this.control.statusChanges : EMPTY,
    ).pipe(
      tap((event) => {
        // console.log('FormErrorDirective got event', event, this.control);

        // if (this.externalValidationSrc) {
        //   if (this.externalValidationSrc.getValue() === false) {
        //     const error = this.getFormError();
        //     setTimeout(() => {
        //       this.setError(error, false);
        //     }, 0)
        //   }
        //   return;
        // }

        if (this.control?.touched || [ 'submitted', 'VALID' ].includes(event)) {
          const error = this.getFormError();

          if (this.debugId) {
            console.log('AppFV: error', error);
          }

          setTimeout(() => {
            this.setError(error);
          }, 0);
        }
      }),
    );
  }

  setError(error: IFormError) {
    if (this.validationDisabled) {
      return;
    }

    if (!this.errorComponentRef && !this.onlyBorder) {
      const factory = this.resolver.resolveComponentFactory(FormErrorComponent);
      this.errorComponentRef = this.container.createComponent(factory);
    }
    if (this.errorComponentRef) {
      this.errorComponentRef.instance.text = error?.text;
    }

    this.toggleErrorAndBorder(error);
  }

  private toggleErrorAndBorder(error: IFormError | boolean) {
    this.toggleBorderByFocus(this.globalFocusStatus$.getValue());
    this.toggleErrorComponent(this.globalFocusStatus$.getValue())
    this.toggleBorderColor(!!error);
  }

  private getFormError(): IFormError {
    const controlErrors = this.control.errors;
    if (controlErrors) {
      const firstKey = Object.keys(controlErrors).slice(-1)[0];
      const getError = this.errors[firstKey] || this.errors['default'];
      const text = getError(controlErrors[firstKey]);
      return { key: firstKey, text };
    } else {
      return null;
    }
  }

  private addClassToEl(el: ViewContainerRef | ElementRef | HTMLElement, className: string) {
    this.renderer2.addClass(el, className);
  }

  private removeClassFromEl(el: ViewContainerRef | ElementRef | HTMLElement, className: string) {
    this.renderer2.removeClass(el, className);
  }

  private commonToggleClass(el: ViewContainerRef | ElementRef | HTMLElement, className: string, addCondition: boolean) {
    if (addCondition) {
      this.addClassToEl(el, className);
    } else {
      this.removeClassFromEl(el, className);
    }
  }

  toggleBorderColor(hasError: boolean) {
    if (this.externalManagedElements?.length) {
      this.externalManagedElements.forEach(el => {
        this.commonToggleClass(el, CssClasses.fvError, hasError);
      })
    }

    if (this.borderContainer) {
      this.commonToggleClass(this.borderContainer, CssClasses.fvError, hasError);
    }
  }

  private toggleErrorComponent(focused: boolean) {
    if (this.errorComponentRef) {
      this.errorComponentRef.instance.enabled = !focused;
    }
  }

  private toggleBorderByFocus(focused: boolean) {
    if (this.borderContainer) {
      this.commonToggleClass(this.borderContainer, CssClasses.fvFocused, focused);
    }
  }

  private toggleUntouchedClass(touched: boolean) {
    if (this.borderContainer) {
      this.commonToggleClass(this.borderContainer, 'ng-untouched', !touched);
    }
  }

  private trackFocus() {
    this.activityService.pointerEvent$.pipe(untilDestroyed(this)).subscribe({
      next: (event) => {
        // console.log(event, event.target, this.nativeElement);
        if (!event.target || !this.nativeElement) {
          return;
        }
        const target = event.target;
        if (target instanceof Node) {
          const inside = this.nativeElement.contains(target);
          if (inside) {
            this.globalFocusStatus$.next(true);
            return;
          }
          if (!inside) {
            if (this.overlayMarker) {
              const inMarkedOverlay = this.checkIfInMarkeredOverlay(event.target as HTMLElement,
                this.overlayMarker);
              // console.log('inMarkedOverlay', inMarkedOverlay);
              if (inMarkedOverlay) {
                this.globalFocusStatus$.next(true);
                return;
              }
            }
          }
        }
        this.globalFocusStatus$.next(false);
        return;
      }
    })
  }

  private checkIfInMarkeredOverlay(el: HTMLElement, marker: string) {
    if (!el || !el.classList || !el.tagName) {
      return false;
    }
    if (el.classList.contains(marker)) {
      return true;
    } else {
      if (el.tagName === 'body') {
        return false;
      } else {
        return this.checkIfInMarkeredOverlay(el.parentElement, marker);
      }
    }
  }
}
